import React, { ReactNode } from 'react';
import { OptionType } from '@ru-digital-assets/ui-component/dist/stories/ui/ui-select/ui-select';
import GridBorder from '../../modules/grid-border/grid-border';
import LabelValueItem from '../../modules/label-value-item/label-value-item';
import Step from '../../modules/step';
import TableHeaderWrapper from '../../modules/table-header-wrapper/table-header-wrapper';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import currencyFormat from '../../utils/currencyFormat';
import SignForm from '../forms/sign-form/sign-form';
import TransactionDetailsTable from 'components/TransactionDetailsTable';
import AssetsDetailsDocs from './assets-details-docs/assets-details-docs';
import AssetsDetailsInfoBody from './assets-details-info-body/assets-details-info-body';
import AssetsDetailsTop from './assets-details-top/assets-details-top';

import './assets-details-wrapper.scss';

interface AssetsDetailsWrapperProps {
  options: { value: string; label: string }[];
  onCertSelect: (certificate: OptionType | OptionType[] | undefined) => void;
  isDisabled?: boolean;
  isCredit?: boolean;
  isSale?: boolean;
  isRelease?: boolean;
  totalSum?: number;
  fromName?: string;
  toName?: string;
  docs: React.ReactNode[];
  from?: {
    name: string;
    inn: string;
    hash?: string;
  };
  to?: {
    name: string;
    inn: string;
    hash?: string;
  };
  creditInfo?: {
    creditorName: string;
    sum: number;
    rate: number;
    creditSum: number;
    fromDate: string;
    toDate: string;
  };
  onClose: () => void;
  onSubmit: () => void;
  selectedCert: OptionType | undefined;
  rightContent?: ReactNode;
}

const AssetsDetailsWrapper: React.FC<AssetsDetailsWrapperProps> = ({
  isDisabled,
  isSale,
  docs,
  totalSum,
  isCredit,
  options,
  onCertSelect,
  creditInfo,
  onSubmit,
  onClose,
  from,
  to,
  isRelease,
  selectedCert,
  fromName,
  toName,
  rightContent,
}) => {
  const getOperationDesc = () => {
    if (isCredit)
      return `Заём финансовых активов на сумму ${currencyFormat(
        creditInfo?.sum || 0
      )}`;

    if (isSale) return `Продажа финансовых активов на сумму`;

    if (isRelease) return `Выпуск цифровых финансовых активов на сумму`;

    return '';
  };

  return (
    <TableWrapper>
      <div className="assets-details">
        <div className="assets-details__header">
          <TableHeaderWrapper
            onCloseClick={onClose}
            isOrange
            leftContent="Проверьте и подпишите операцию"
          />
        </div>
        <div className="assets-details__body">
          <GridBorder
            infoTitle={
              <>
                <Step
                  steps={[
                    { title: 'Параметры' },
                    { title: 'Подпись', active: true },
                    { title: 'Согласование' },
                    { title: isRelease ? 'Выпуск' : 'Подтверждение оплаты' },
                  ]}
                />
                <AssetsDetailsTop
                  title="Операция:"
                  desc={getOperationDesc()}
                  sum={totalSum ?? 0}
                />
              </>
            }
            leftContent={
              <div className="assets-details__col assets-details__col-12">
                {from && to ? (
                  <>
                    {!isRelease && (
                      <h2 className="text-2 assets-details__title">
                        {isCredit ? 'Контрагены:' : 'Детали операции:'}
                      </h2>
                    )}
                    <AssetsDetailsInfoBody
                      fromName={fromName}
                      toName={toName}
                      from={from}
                      to={to}
                    />
                  </>
                ) : null}
                {!!docs.length && (
                  <>
                    <h2 className="text-2 assets-details__title">Документы:</h2>
                    <AssetsDetailsDocs docs={docs} />
                  </>
                )}
                {!isCredit && from?.hash !== '-' && (
                  <h2 className="text-2 assets-details__title">
                    Детали транзакции:
                  </h2>
                )}
                {(isSale || isRelease) && from?.hash !== '-' && (
                  <TransactionDetailsTable
                    fromHash={from?.hash || '-'}
                    toHash={to?.hash || '-'}
                    fromName={from?.name || '-'}
                    toName={to?.name || '-'}
                    transactionType={isSale ? 'sale' : 'issue'}
                  />
                )}
                <div className="assets-details__form">
                  <SignForm
                    onSubmit={onSubmit}
                    isDisabled={isDisabled}
                    options={options}
                    selectedCert={selectedCert}
                    onChangeCert={onCertSelect}
                  />
                </div>
              </div>
            }
            rightContent={
              rightContent ||
              (isCredit && creditInfo ? (
                <div className="assets-details__col assets-details__col-12">
                  <h2 className="text-2 assets-details__title">
                    Информация по займу:
                  </h2>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Кредитор:"
                      text={creditInfo.creditorName}
                    />
                  </div>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Сумма займа:"
                      text={currencyFormat(creditInfo.sum)}
                    />
                  </div>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Ставка:"
                      text={`${creditInfo.rate.toString()} %`}
                    />
                  </div>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Стоимость займа:"
                      text={currencyFormat(creditInfo.creditSum)}
                    />
                  </div>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Дата взятия займа:"
                      text={creditInfo.fromDate}
                    />
                  </div>
                  <div className="assets-details__item">
                    <LabelValueItem
                      title="Дата выплаты:"
                      text={creditInfo.toDate}
                    />
                  </div>
                </div>
              ) : null)
            }
          />
        </div>
      </div>
    </TableWrapper>
  );
};

export default AssetsDetailsWrapper;
